import React, { useEffect, useRef } from "react";
import { useDocusealEmbed } from "@/hooks/use-docuseal-embed";

interface DocusealFormEmbedProps {
  src: string;
  scriptUrl: string;
  onCompleted: () => void;
}

/**
 * Renders a DocuSeal embedded signing form (`<docuseal-form>` web component):
 * injects the script + expands its shadow DOM (via useDocusealEmbed) and wires
 * up the `completed` custom event.
 */
export function DocusealFormEmbed({
  src,
  scriptUrl,
  onCompleted,
}: DocusealFormEmbedProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useDocusealEmbed({
    wrapperRef,
    elementTag: "docuseal-form",
    scriptUrl,
    resetKey: src,
  });

  // Wire up the `completed` event on the web component element itself.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    function handleCompleted() {
      onCompletedRef.current();
    }

    // The element may not exist yet when this effect runs; observe for it.
    const host = wrapper.querySelector("docuseal-form");
    if (host) {
      host.addEventListener("completed", handleCompleted);
      return () => host.removeEventListener("completed", handleCompleted);
    }

    let observedHost: Element | null = null;
    const observer = new MutationObserver(() => {
      const el = wrapper.querySelector("docuseal-form");
      if (el) {
        observer.disconnect();
        observedHost = el;
        observedHost.addEventListener("completed", handleCompleted);
      }
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      observedHost?.removeEventListener("completed", handleCompleted);
    };
  }, [src]);

  return (
    <div className="w-full" ref={wrapperRef}>
      {React.createElement("docuseal-form", {
        "data-src": src,
        style: { display: "block" },
      })}
    </div>
  );
}
